// GNÅ ↔ SKADE bro: adaptere som parser GNÅ-appens EKSAKTE Matrix-wire-skjema
// (samme JSON som lib/screens/{tasking,reports,eso}/*.dart produserer) og mapper
// til SKADE-kanoniske modeller — og tilbake. Dette er «én sannhet»-limet: det
// feltappen sender (dagsedler, RFI, stridsmeldinger, ESO klar-tre) dukker opp i
// SKADE-COP-en uten divergens, når Matrix-broen kobler timeline → hub.
//
// Rent + testbart. Alle parsere er defensive (null ved ugyldig), aldri kast.
import { z } from 'zod';
import { RfiSchema } from './rfi.js';
import { CombatReportSchema } from './report.js';
import { mgrsToLatLon } from './targeting.js';
import { CalendarActivitySchema } from './calendar.js';
// ── Felles ──────────────────────────────────────────────────────────────────
function tryObj(body) {
    const t = body.trim();
    if (!t.startsWith('{'))
        return null;
    try {
        const d = JSON.parse(t);
        return d && typeof d === 'object' && !Array.isArray(d) ? d : null;
    }
    catch {
        return null;
    }
}
const asNum = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const asStr = (v) => (typeof v === 'string' ? v : undefined);
// ── RFI (GNÅ tasking: gna_rfi / gna_rfi_response v1) ────────────────────────
/** GNÅ `gna_rfi` → SKADE Rfi. `by`/senderId = requester (autentisert vinner). */
export function parseGnaRfi(body, meta = {}) {
    const m = tryObj(body);
    if (!m || m['gna_rfi'] !== 1)
        return null;
    const id = asStr(m['id']);
    const text = asStr(m['text']);
    if (!id || !text)
        return null;
    const deadline = asNum(m['deadline']);
    const parsed = RfiSchema.safeParse({
        id,
        requester: meta.senderId ?? asStr(m['by']) ?? '',
        question: text,
        priority: 'routine',
        status: 'open',
        createdMs: meta.tsMs ?? 0,
        dueMs: deadline,
        lat: asNum(m['lat']),
        lon: asNum(m['lon']),
        radiusM: asNum(m['radius_m']),
    });
    return parsed.success ? parsed.data : null;
}
/** SKADE Rfi → GNÅ `gna_rfi`-nyttelast (så COP-opprettede RFI leses av GNÅ). */
export function rfiToGnaRfi(r) {
    return {
        gna_rfi: 1,
        id: r.id,
        text: r.question,
        lat: r.lat ?? 0,
        lon: r.lon ?? 0,
        radius_m: r.radiusM ?? 0,
        deadline: r.dueMs ?? null,
        by: r.requester,
    };
}
/** GNÅ `gna_rfi_response` → normalisert svar (kan brukes til answerRfi). */
export function parseGnaRfiResponse(body, meta = {}) {
    const m = tryObj(body);
    if (!m || m['gna_rfi_response'] !== 1)
        return null;
    const rfiId = asStr(m['rfi_id']);
    const text = asStr(m['text']);
    if (!rfiId || text === undefined)
        return null;
    return { rfiId, text, by: meta.senderId ?? asStr(m['by']) ?? '', lat: asNum(m['lat']), lon: asNum(m['lon']) };
}
// ── Stridsmelding (GNÅ reports: gna_report v1) ──────────────────────────────
/** GNÅ report-type (wire) → SKADE ReportType. Ukjent type → null.
 *
 *  `melding` MÅ stå her: det er Forsvarets standard stridsmelding og GNÅs DEFAULT
 *  rapporttype (første i velgeren, se lib/screens/reports/report_models.dart). Manglet
 *  den, returnerte parseGnaReport null, og hub-en droppet meldingen UTEN logglinje —
 *  altså stille tap av den mest brukte meldingstypen. SKADE har alltid hatt 'MELDING'
 *  i ReportType + REPORT_FIELDS; det var kun dette oppslaget som manglet. */
const GNA_REPORT_TYPE = {
    melding: 'MELDING',
    salute: 'SALUTE',
    tic: 'CONTACT',
    medevac9: 'MEDEVAC',
    sitrep: 'SITREP',
    uxo: 'UXO',
    cbrn: 'CBRN',
};
/** GNÅ `gna_report` → SKADE CombatReport. Typede felt legges i fields (som
 * strenger). Dekker salute/tic/medevac9/sitrep/uxo/cbrn. */
export function parseGnaReport(body, meta = {}) {
    const m = tryObj(body);
    if (!m || m['gna_report'] !== 1)
        return null;
    const wire = asStr(m['type']);
    const type = wire ? GNA_REPORT_TYPE[wire] : undefined;
    const id = asStr(m['id']);
    if (!type || !id)
        return null;
    // Posisjon: lat/lon når GPS er tilgjengelig, ellers MGRS skrevet inn manuelt.
    // GNÅ tillater bevisst rapport UTEN GPS-fix (GeoPoint med kun `mgrs`) — det er
    // NORMALEN under GPS-nekt/EMCON. Å kreve lat/lon her forkastet hele meldingen,
    // inkludert MEDEVAC, uten spor. MGRS er en fullgod posisjon; vi konverterer den.
    let lat = asNum(m['lat']);
    let lon = asNum(m['lon']);
    if (lat === undefined || lon === undefined) {
        const mgrs = asStr(m['mgrs']);
        const fromMgrs = mgrs ? mgrsToLatLon(mgrs) : null;
        if (!fromMgrs)
            return null; // verken lat/lon eller tolkbar MGRS → ingen posisjon
        lat = fromMgrs.lat;
        lon = fromMgrs.lon;
    }
    // Samle øvrige skalar-felt som strenger til fields (hopper over metadata).
    const skip = new Set(['gna_report', 'type', 'id', 'ts', 'lat', 'lon', 'by']);
    const fields = {};
    for (const [k, v] of Object.entries(m)) {
        if (skip.has(k))
            continue;
        if (typeof v === 'string')
            fields[k] = v.slice(0, 4000);
        else if (typeof v === 'number' || typeof v === 'boolean')
            fields[k] = String(v);
    }
    const tsMs = asNum(m['ts']) ?? meta.tsMs ?? 0;
    const parsed = CombatReportSchema.safeParse({
        id, type, lat, lon, tsMs,
        author: meta.senderId ?? asStr(m['by']) ?? '',
        fields, status: 'ny', updatedMs: tsMs, thread: [],
    });
    return parsed.success ? parsed.data : null;
}
// ── Dagsedel / tjenesteliste (GNÅ agenda: gna=agenda.activity.v1/delete.v1) ──
export const AgendaActivitySchema = z.object({
    id: z.string().max(64),
    startMs: z.number(),
    durationMin: z.number().int().min(0).max(100000).default(0),
    title: z.string().max(500),
    place: z.string().max(500).default(''),
    desc: z.string().max(4000).default(''),
    editedMs: z.number().default(0),
});
/** GNÅ agenda-melding → hendelse (upsert/delete), eller null (vanlig melding). */
export function parseGnaAgenda(body) {
    const m = tryObj(body);
    if (!m)
        return null;
    const disc = m['gna'];
    if (disc === 'agenda.activity.v1') {
        const parsed = AgendaActivitySchema.safeParse({
            id: m['id'], startMs: m['startMs'], durationMin: m['durationMin'],
            title: m['title'], place: m['place'], desc: m['desc'], editedMs: m['editedMs'],
        });
        return parsed.success ? { kind: 'upsert', activity: parsed.data } : null;
    }
    if (disc === 'agenda.delete.v1') {
        const id = asStr(m['id']);
        if (!id)
            return null;
        return { kind: 'delete', id, editedMs: asNum(m['editedMs']) ?? 0 };
    }
    return null;
}
/** Reduser tidsordnede agenda-hendelser til gjeldende dagsedel (siste-vinner +
 * slette-gravstein), speiler GNÅ `reduceAgenda`. Sortert på starttid. */
export function reduceAgenda(events) {
    const current = new Map();
    const deletedAt = new Map();
    const lastEdit = new Map();
    for (const e of events) {
        if (e.kind === 'upsert') {
            const a = e.activity;
            if (a.editedMs >= (lastEdit.get(a.id) ?? -1)) {
                lastEdit.set(a.id, a.editedMs);
                const del = deletedAt.get(a.id);
                if (del === undefined || a.editedMs > del) {
                    current.set(a.id, a);
                    deletedAt.delete(a.id);
                }
            }
        }
        else {
            if (e.editedMs >= (deletedAt.get(e.id) ?? -1)) {
                deletedAt.set(e.id, e.editedMs);
                const edit = lastEdit.get(e.id);
                if (edit === undefined || e.editedMs >= edit)
                    current.delete(e.id);
            }
        }
    }
    return [...current.values()].sort((a, b) => a.startMs - b.startMs);
}
/** GNÅ ESO-enkeltmannsstatus → normalisert. senderId binder identitet (anti-spoof). */
export function parseGnaEso(body, meta = {}) {
    const m = tryObj(body);
    if (!m || m['gna'] !== 'eso.status.v1')
        return null;
    const soldier = meta.senderId ?? asStr(m['soldier']);
    if (!soldier)
        return null;
    const checkedArr = Array.isArray(m['checked']) ? m['checked'] : [];
    return {
        soldierId: soldier,
        squad: asStr(m['squad']) ?? '—',
        ready: m['ready'] === true,
        checked: checkedArr.length,
        total: asNum(m['total']) ?? 0,
        tsMs: asNum(m['tsMs']) ?? meta.tsMs ?? 0,
    };
}
/** Aggreger ESO-statuser til klar-andel per lag (nyeste per soldat vinner) —
 * mater ORBAT-stridsevne (strengthReadiness) fra feltets faktiske klar-melding. */
export function reduceEsoReadiness(statuses) {
    const latest = new Map();
    for (const s of statuses) {
        const cur = latest.get(s.soldierId);
        if (!cur || s.tsMs >= cur.tsMs)
            latest.set(s.soldierId, s);
    }
    const bySquad = new Map();
    for (const s of latest.values()) {
        const g = bySquad.get(s.squad) ?? { ready: 0, strength: 0 };
        g.strength++;
        if (s.ready)
            g.ready++;
        bySquad.set(s.squad, g);
    }
    return [...bySquad.entries()]
        .map(([squad, g]) => ({ squad, ready: g.ready, strength: g.strength, ratio: g.strength ? g.ready / g.strength : 0 }))
        .sort((a, b) => a.squad.localeCompare(b.squad));
}
// ── Dagsedler: GNÅ-agenda → SKADE-kalenderaktivitet ────────────────────────
// `parseGnaAgenda` fantes, men ingenting oversatte resultatet til SKADE-siden.
// En dagsedel lagt inn i appen var derfor usynlig i COP-kalenderen. De to
// modellene er ULIKE:
//
//   GNÅ AgendaActivity:     {id, startMs, durationMin, title, place, desc, editedMs}
//   SKADE CalendarActivity: {id, title, startMs, endMs, issuedBy, createdBy, …}
//
// GNÅ har VARIGHET (minutter), SKADE har SLUTTIDSPUNKT. GNÅ har fritekst-«place»,
// SKADE har valgfri koordinat + etikett. GNÅ har ingen utsteder (den er implisitt
// Matrix-avsenderen). Oversettelsen er derfor en berikelse, ikke en omdøping —
// og den skjer på ETT sted.
/**
 * GNÅ-agenda-hendelse → SKADE-kalenderaktivitet, eller null når nyttelasten
 * ikke er en agenda-melding.
 *
 * [enhet] er utsteder-enheten aktiviteten tilskrives (rom→org fra broen).
 * [senderId] blir `createdBy` — den autentiserte avsenderen, aldri et JSON-felt.
 *
 * En sletting returnerer `{ slett: id }`: SKADE-kalenderen reduserer gravsteiner
 * selv, og å fabrikkere en tom aktivitet ville løyet om at den fortsatt finnes.
 */
export function agendaTilKalender(body, opts) {
    const h = parseGnaAgenda(body);
    if (!h)
        return null;
    if (h.kind === 'delete')
        return { slett: h.id, editedMs: h.editedMs };
    const a = h.activity;
    // GNÅ oppgir varighet; SKADE trenger slutt. En dagsedel uten varighet er et
    // punkt i tid (startMs === endMs), ikke noe som varer evig.
    const endMs = a.startMs + Math.max(0, a.durationMin) * 60_000;
    const parsed = CalendarActivitySchema.safeParse({
        id: a.id,
        title: a.title || '(uten tittel)',
        type: 'annet',
        status: 'forslag', // en dagsedel fra felt er et FORSLAG til TOC, ikke et vedtak
        startMs: a.startMs,
        endMs,
        allDay: false,
        issuedBy: opts.enhet,
        assignedTo: [],
        createdBy: opts.senderId ?? '',
        // `place` er fritekst i GNÅ og kan ikke antas å være en koordinat. Den går
        // i beskrivelsen, ikke i `location`: en oppdiktet koordinat på kartet er
        // verre enn ingen koordinat.
        description: [a.place && `Sted: ${a.place}`, a.desc].filter(Boolean).join('\n') || undefined,
        // `tsMs` er SKADEs endringstid og styrer mergeActivity (nyeste vinner).
        // GNÅ sin `editedMs` er samme begrep — bruk den, ellers ville en dagsedel
        // fra felt alltid tapt mot en TOC-kopi med ts=0.
        tsMs: a.editedMs,
    });
    return parsed.success ? { aktivitet: parsed.data } : null;
}
//# sourceMappingURL=gna-bridge.js.map